import { useEffect, useMemo, useReducer, useRef } from 'react'
import type { EvidenceKind } from './learningEvidence'
import type { AttemptDetails } from '../types/progress'

export type LessonEngineStatus = 'INTRO' | 'QUESTION' | 'ANSWERED' | 'FEEDBACK' | 'NEXT' | 'COMPLETE'

export interface LessonEngineState {
  status: LessonEngineStatus
  correctCount: number
  incorrectCount: number
  streak: number
  lastAnswerCorrect: boolean | null
}

type Event =
  | { type: 'START' }
  | { type: 'SUBMIT'; correct: boolean }
  | { type: 'SHOW_FEEDBACK' }
  | { type: 'ADVANCE' }
  | { type: 'FINISH' }

export const initialState: LessonEngineState = {
  status: 'INTRO',
  correctCount: 0,
  incorrectCount: 0,
  streak: 0,
  lastAnswerCorrect: null,
}

export const reducer = (state: LessonEngineState, event: Event): LessonEngineState => {
  switch (event.type) {
    case 'START':
      if (state.status !== 'INTRO') return state
      return { ...state, status: 'QUESTION' }
    case 'SUBMIT':
      if (state.status !== 'QUESTION') return state
      return {
        ...state,
        status: 'ANSWERED',
        correctCount: state.correctCount + (event.correct ? 1 : 0),
        incorrectCount: state.incorrectCount + (event.correct ? 0 : 1),
        streak: event.correct ? state.streak + 1 : 0,
        lastAnswerCorrect: event.correct,
      }
    case 'SHOW_FEEDBACK':
      if (state.status !== 'ANSWERED') return state
      return { ...state, status: 'FEEDBACK' }
    case 'ADVANCE':
      if (state.status !== 'FEEDBACK') return state
      return { ...state, status: state.lastAnswerCorrect ? 'NEXT' : 'QUESTION' }
    case 'FINISH':
      if (state.status !== 'NEXT') return state
      return { ...state, status: 'COMPLETE' }
  }
}

interface LessonEngineOptions {
  onAttempt(correct: boolean, evidence: EvidenceKind, details?: AttemptDetails): void
  onComplete(): void
}

export const useLessonEngine = ({ onAttempt, onComplete }: LessonEngineOptions) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const completed = useRef(false)
  const submitting = useRef(false)
  useEffect(() => { if (state.status === 'QUESTION') submitting.current = false }, [state.status])

  useEffect(() => {
    if (state.status !== 'ANSWERED') return
    const timer = window.setTimeout(() => dispatch({ type: 'SHOW_FEEDBACK' }), 160)
    return () => window.clearTimeout(timer)
  }, [state.status])

  useEffect(() => {
    if (state.status !== 'FEEDBACK') return
    const timer = window.setTimeout(() => dispatch({ type: 'ADVANCE' }), state.lastAnswerCorrect ? 950 : 900)
    return () => window.clearTimeout(timer)
  }, [state.lastAnswerCorrect, state.status])

  useEffect(() => {
    if (state.status !== 'NEXT') return
    const timer = window.setTimeout(() => dispatch({ type: 'FINISH' }), 180)
    return () => window.clearTimeout(timer)
  }, [state.status])

  useEffect(() => {
    if (state.status !== 'COMPLETE' || completed.current) return
    completed.current = true
    onComplete()
  }, [onComplete, state.status])

  return useMemo(() => ({
    state,
    start: () => dispatch({ type: 'START' }),
    answer: (correct: boolean, evidence: EvidenceKind = 'checked', details?: AttemptDetails) => {
      if (state.status !== 'QUESTION' || submitting.current) return
      submitting.current = true
      onAttempt(correct, evidence, details)
      dispatch({ type: 'SUBMIT', correct })
    },
  }), [onAttempt, state])
}
